/* Editorial Tenant Portfolio — magazine-grade mosaic on the tenant's
   canvas. Fully theme-reactive (reads --tenant-bg / --tenant-fg /
   --tenant-accent) so flipping the studio's theme paints the whole
   surface, not just the accent word. Replaces the prior re-export of
   the founder cream Portfolio which collided with the Editorial dark
   Layout. */

import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { getFocalStyle } from "@/lib/focal";
import { tenantHref } from "@/lib/tenant";
import { optimizeImageUrl, optimizedSrcSet } from "@/lib/imageOptim";
import { ArrowRight } from "lucide-react";
import { EditorialSkeleton } from "./_EditorialSkeleton";

const UNS = "https://images.unsplash.com";

// Caption + aspect rotation we apply to studio-provided portfolio_images.
// This is layout sugar only — NOT a fallback image source. When a studio
// hasn't uploaded any portfolio images yet, the page renders an empty
// "Portfolio in progress" state instead of stock Unsplash photos that
// were leaking Allure's curated demo work onto every new tenant's site.
const TILE_LAYOUT = [
  { caption: "",  aspect: "3 / 2", big: true },
  { caption: "",  aspect: "3 / 4" },
  { caption: "",  aspect: "4 / 5" },
  { caption: "",  aspect: "3 / 4" },
  { caption: "",  aspect: "3 / 2" },
  { caption: "",  aspect: "4 / 5" },
  { caption: "",  aspect: "3 / 4" },
  { caption: "",  aspect: "3 / 2" },
];

export default function EditorialPortfolio() {
  const { settings, loading } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const studioLocation = settings?.studio_location || settings?.location || "the studio";

  useEffect(() => {
    document.title = `${studioName} — Portfolio`;
  }, [studioName]);

  // Build shots strictly from the studio's portfolio_images. No fallback to
  // stock photography — if a brand-new studio hasn't uploaded any work yet,
  // we show an empty-state block below rather than borrow Allure's curated
  // demo set (which used to leak across every tenant pre-2026-02 audit).
  const portfolio = Array.isArray(settings?.portfolio_images) ? settings.portfolio_images : [];
  const focals = settings?.portfolio_focal_points || [];
  const shots = portfolio.map((src, i) => ({
    src,
    origIndex: i,           // portfolio-array index so we can look up focal point
    caption: TILE_LAYOUT[i % TILE_LAYOUT.length].caption,
    aspect: TILE_LAYOUT[i % TILE_LAYOUT.length].aspect,
    big: i === 0,
  }));

  const hero = shots[0];
  const rest = shots.slice(1);
  // Asymmetric 3-2-3 pattern that REPEATS for the full portfolio length.
  // Previously this hardcoded [rest.slice(0,3), rest.slice(3,5), rest.slice(5,8)]
  // which silently discarded every image past #8 — studios uploading a 15-
  // or 30-image portfolio only ever showed the first 9 photos. Fix: chunk
  // `rest` into rows of [3, 2, 3, 3, 2, 3, ...] so every uploaded image
  // makes it to the page (last row may be a partial 1- or 2-tile row).
  const ROW_PATTERN = [3, 2, 3];
  const rows = [];
  {
    let cursor = 0;
    let p = 0;
    while (cursor < rest.length) {
      const take = ROW_PATTERN[p % ROW_PATTERN.length];
      rows.push(rest.slice(cursor, cursor + take));
      cursor += take;
      p += 1;
    }
  }

  // Editorial row prefaces — short prose between groups so the page reads
  // like a magazine spread and gives crawlers substantive copy to index.
  // Studios can override these via portfolio_intro / portfolio_row_notes in
  // site_settings; otherwise we fall back to editorial-voice defaults.
  const introCopy = settings?.portfolio_intro
    || `This is a curated cross-section of ${studioName}'s editorial portrait work — sittings, location stories, and milestone sessions, edited down to the frames we'd hang on our own wall. We've left out the safe shots and the polite ones; what's here is the work we'd repeat.`;
  const approachCopy = settings?.portfolio_approach
    || `Our approach is unhurried. A typical sitting lasts ninety minutes to four hours; we shoot fewer frames than most contemporaries because we'd rather pre-visualise an image than fix it in the edit. Every session begins with a short conversation — about wardrobe, the room we'll photograph in, the moment you'd like remembered — and ends with a delivered set of cinema-graded conversions rather than a flood of nearly-identical exposures.`;
  const orientCopy = settings?.portfolio_orient
    || `Captions name the subject and series. To commission a sitting in this vein, write to ${studioLocation === "the studio" ? "the studio" : `the studio in ${studioLocation}`} or reserve a date directly through the booking page.`;

  // First-time visitors see the skeleton during the /api/site fetch window
  // so we never flash an empty state on a studio that DOES have portfolio
  // images stored — only on settled-empty studios. Returning visitors
  // hydrate from localStorage and skip this branch instantly. Placed AFTER
  // all hooks to honor Rules of Hooks.
  if (loading && !settings) {
    return <EditorialSkeleton eyebrow="Loading the portfolio." />;
  }

  return (
    <div
      data-testid="editorial-tenant-portfolio"
      className="min-h-screen pt-28 pb-24"
      style={{ background: "var(--tenant-bg, #0A0908)", color: "var(--tenant-fg, #F5F2EC)" }}
    >
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
        {/* Chapter masthead */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--tenant-accent)" }}>
            Chapter III · The Work
          </div>
          <div
            className="mt-5 h-px mx-auto"
            style={{ width: "3rem", background: "var(--tenant-accent)" }}
            aria-hidden
          />
          <h1
            className="mt-10 leading-[0.95] tracking-tight"
            style={{
              fontFamily: "var(--tenant-serif)",
              fontWeight: 300,
              fontSize: "clamp(3rem, 8vw, 6.5rem)",
            }}
          >
            Portfolio
          </h1>
          <p
            className="mt-8 text-[10px] uppercase tracking-[0.38em]"
            style={{ color: "var(--tenant-accent)" }}
          >
            A SELECTION · {shots.length} SESSIONS
          </p>
        </div>

        {/* Editorial preface — substantive prose so the page reads like a
            magazine cover and gives search engines real content to index. */}
        <div className="mt-14 lg:mt-16 max-w-3xl mx-auto" data-testid="editorial-portfolio-intro">
          <p
            className="text-lg leading-relaxed"
            style={{ color: "var(--tenant-fg-soft, rgba(245,242,236,0.8))" }}
          >
            {introCopy}
          </p>
          <p
            className="mt-6 leading-relaxed"
            style={{ color: "var(--tenant-fg-soft, rgba(245,242,236,0.7))" }}
          >
            {approachCopy}
          </p>
          <p
            className="mt-6 leading-relaxed"
            style={{ color: "var(--tenant-fg-soft, rgba(245,242,236,0.7))" }}
          >
            {orientCopy}
          </p>
        </div>

        {/* Hero shot — full bleed within the gutter. Only rendered when the
            studio has uploaded at least one portfolio image. */}
        {hero ? (
          <figure className="mt-16">
            <div className="relative w-full overflow-hidden" style={{ aspectRatio: hero.aspect }}>
              <img
                src={optimizeImageUrl(hero.src, { w: 2000 })}
                srcSet={optimizedSrcSet(hero.src, [800, 1200, 1600, 2000, 2400]) || undefined}
                sizes="(max-width: 1024px) 100vw, 1480px"
                alt={hero.caption}
                className="absolute inset-0 w-full h-full object-cover"
                style={getFocalStyle(focals, hero.origIndex)}
                loading="eager"
                fetchpriority="high"
                decoding="async"
              />
            </div>
            {hero.caption && (
              <figcaption
                className="mt-5 text-center text-base italic"
                style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-fg-soft, rgba(245,242,236,0.65))" }}
              >
                {hero.caption}
              </figcaption>
            )}
          </figure>
        ) : (
          <div
            className="mt-20 mx-auto max-w-2xl text-center py-20 border"
            style={{ borderColor: "var(--tenant-fg-line, rgba(245,242,236,0.15))" }}
            data-testid="editorial-portfolio-empty"
          >
            <div className="text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--tenant-accent)" }}>
              Portfolio in progress
            </div>
            <div
              className="mt-5 mx-auto h-px"
              style={{ width: "2rem", background: "var(--tenant-accent)" }}
              aria-hidden
            />
            <p
              className="mt-7 leading-relaxed"
              style={{ color: "var(--tenant-fg-soft, rgba(245,242,236,0.75))" }}
            >
              {studioName} is preparing its first published selection. The portfolio will be live here shortly &mdash; in the meantime, you&rsquo;re welcome to reach out directly to discuss a sitting or request a private viewing.
            </p>
            <Link
              to={tenantHref("/contact")}
              className="inline-flex items-center gap-3 mt-9 px-7 h-12 text-[10px] uppercase tracking-[0.38em] transition-colors"
              style={{
                background: "var(--tenant-fg, #F5F2EC)",
                color: "var(--tenant-bg, #0A0908)",
              }}
              data-testid="editorial-portfolio-empty-cta"
            >
              Contact the studio <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}

        {/* Asymmetric rows */}
        {rows.map((row, ri) => (
          <div key={ri}>
            <div
              className="my-14 h-px"
              style={{ background: "var(--tenant-fg-line, rgba(245,242,236,0.15))" }}
              aria-hidden
            />
            <div
              className={`grid grid-cols-1 gap-10 lg:gap-14 ${
                row.length === 3 ? "md:grid-cols-3" : row.length === 2 ? "md:grid-cols-2" : "md:grid-cols-1"
              }`}
            >
              {row.map((s, i) => (
                <figure key={`${s.src}-${i}`} data-testid={`editorial-portfolio-tile-${ri}-${i}`}>
                  <div
                    className="relative w-full overflow-hidden"
                    style={{
                      aspectRatio: s.aspect,
                      background: "var(--tenant-fg-line, rgba(245,242,236,0.08))",
                    }}
                  >
                    <img
                      src={optimizeImageUrl(s.src, { w: 1200 })}
                      srcSet={optimizedSrcSet(s.src, [600, 900, 1200, 1600]) || undefined}
                      sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 480px"
                      alt={s.caption}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover"
                      style={getFocalStyle(focals, s.origIndex)}
                    />
                  </div>
                  <figcaption
                    className="mt-4 text-sm italic"
                    style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-fg-soft, rgba(245,242,236,0.65))" }}
                  >
                    {s.caption}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        ))}

        {/* Closing — view full archive + reserve */}
        <div
          className="mt-20 pt-10 border-t flex items-center justify-between gap-6 flex-wrap"
          style={{ borderTopColor: "var(--tenant-fg-line, rgba(245,242,236,0.15))" }}
        >
          <span
            className="text-base italic"
            style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-accent)" }}
          >
            View the full archive on request
          </span>
          <Link
            to={tenantHref("/booking")}
            data-testid="editorial-portfolio-book-cta"
            className="inline-flex items-center gap-3 px-7 h-12 text-[10px] uppercase tracking-[0.38em] transition-colors"
            style={{
              background: "var(--tenant-fg, #F5F2EC)",
              color: "var(--tenant-bg, #0A0908)",
            }}
          >
            Reserve a sitting <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Closing note — short editorial paragraph on the archive +
            commissioning prompt. Adds substantive copy at the end of the
            page so crawlers see a real article-length body, and gives
            visitors who scroll past the grid a clear next step. */}
        <div
          className="mt-16 pt-12 border-t max-w-3xl mx-auto text-center"
          style={{ borderTopColor: "var(--tenant-fg-line, rgba(245,242,236,0.15))" }}
          data-testid="editorial-portfolio-archive-note"
        >
          <div className="text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--tenant-accent)" }}>
            A note on the archive
          </div>
          <div
            className="mt-4 mx-auto h-px"
            style={{ width: "2rem", background: "var(--tenant-accent)" }}
            aria-hidden
          />
          <p
            className="mt-6 leading-relaxed"
            style={{ color: "var(--tenant-fg-soft, rgba(245,242,236,0.75))" }}
          >
            Most of what we make never leaves the studio. The portfolio above is a deliberate edit. If you're researching a particular kind of work and don't see it represented here, the archive is open by request — we're happy to assemble a private gallery of unpublished frames around a brief (a colour palette, a mood, a subject) and walk you through the relevant sittings on a call.
          </p>
        </div>
      </div>
    </div>
  );
}
